import { useEffect, useRef, useState } from "react";
import api from "../api/client";
import { useAuth } from "../store/auth";
import { useToast } from "../store/toast";
import { parseUiPrefs } from "../hooks";
import Icon from "./Icon";

// Configurador de "Mi vista": cada usuario guarda, POR PÁGINA (tablero /
// mis tareas / proyectos), sus criterios de ordenamiento en el orden de
// precedencia que él decida (de arriba hacia abajo), la dirección de cada
// uno y si oculta los completados. Se persiste en users.ui_prefs
// (view_prefs: { [page]: { sort: [{key,dir}], hideCompleted } }).

export const SORT_FIELDS = [
  ["status", "Estado"],
  ["due_date", "Fecha compromiso (vencimiento)"],
  ["completed_date", "Fecha de cumplimiento"],
  ["priority", "Prioridad"],
  ["assignee", "Responsable"],
  ["department", "Departamento"],
];
const LABEL = Object.fromEntries(SORT_FIELDS);

export const DEFAULT_VIEW_PREF = { sort: [], hideCompleted: false };

export function getViewPref(user, page) {
  const vp = parseUiPrefs(user).view_prefs || {};
  return { ...DEFAULT_VIEW_PREF, ...(vp[page] || {}) };
}

const STATUS_RANK = { not_started: 0, in_progress: 1, blocked: 2, completed: 3 };
const PRIORITY_RANK = { critical: 0, high: 1, medium: 2, low: 3 };

function sortVal(t, key) {
  switch (key) {
    case "status": return STATUS_RANK[t.status] ?? 9;
    case "priority": return PRIORITY_RANK[t.priority] ?? 9;
    case "due_date": return t.due_date || "9999-99-99";           // sin fecha al final
    case "completed_date": return t.completed_date || "9999-99-99";
    case "assignee": return (t.assignees?.[0]?.full_name || "￿").toLowerCase();
    case "department": return (t.department_name || "￿").toLowerCase();
    default: return 0;
  }
}

// Orden por defecto (pedido 2026-09-08: "cuando creo una tarea por defecto
// se va al final"): agrupa por urgencia real en vez del orden crudo del API
// — Nuevas (recién creadas) → Por vencer → En proceso → los demás estados →
// Finalizadas al final. Solo se usa cuando el usuario NO definió su propio
// orden en "Mi vista" (pref.sort vacío); si definió uno, se respeta tal cual.
const DAY_MS = 86400000;
const NUEVA_MS = 3 * DAY_MS;   // "nueva" = creada hace menos de 3 días
const VENCE_MS = 3 * DAY_MS;   // "por vencer" = vencida o vence en <=3 días

function defaultCategory(t) {
  if (t.status === "completed") return 4;                 // Finalizadas
  if (t.created_at && Date.now() - new Date(t.created_at).getTime() < NUEVA_MS) return 0;  // Nuevas
  if (t.due_date && new Date(`${t.due_date}T23:59:59`).getTime() <= Date.now() + VENCE_MS) return 1; // Por vencer
  if (t.status === "in_progress") return 2;                // En proceso
  return 3;                                                 // Los demás estados
}

function defaultCompare(a, b) {
  const ca = defaultCategory(a), cb = defaultCategory(b);
  if (ca !== cb) return ca - cb;
  if (ca === 1) {                                           // por vencer: la más próxima primero
    const da = a.due_date || "9999-99-99", db = b.due_date || "9999-99-99";
    if (da !== db) return da < db ? -1 : 1;
  }
  if (ca === 0) {                                           // nuevas: la más reciente primero
    const da = a.created_at || "", db = b.created_at || "";
    if (da !== db) return da > db ? -1 : 1;
  }
  return (a.id || 0) - (b.id || 0);
}

// Aplica la preferencia guardada a una lista de tareas (filtro + multi-orden
// estable con desempate por id). Si no hay criterios, aplica el orden por
// defecto de arriba en vez de devolver la lista sin ordenar.
export function applyViewPref(tasks, pref) {
  let list = pref?.hideCompleted ? tasks.filter((t) => t.status !== "completed") : tasks;
  if (pref?.sort?.length) {
    list = [...list].sort((a, b) => {
      for (const { key, dir } of pref.sort) {
        const m = dir === "desc" ? -1 : 1;
        const va = sortVal(a, key), vb = sortVal(b, key);
        if (va < vb) return -m;
        if (va > vb) return m;
      }
      return (a.id || 0) - (b.id || 0);
    });
  } else {
    list = [...list].sort(defaultCompare);
  }
  return list;
}

export function ViewPrefsButton({ page }) {
  const { user, refreshUser } = useAuth();
  const { push } = useToast();
  const [open, setOpen] = useState(false);
  const [pref, setPref] = useState(() => getViewPref(user, page));
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);

  // Re-sincronizar al abrir (por si se guardó desde otra página/sesión).
  useEffect(() => { if (open) setPref(getViewPref(user, page)); }, [open]); // eslint-disable-line

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const saved = getViewPref(user, page);
  const active = saved.sort.length > 0 || saved.hideCompleted;

  const available = SORT_FIELDS.filter(([k]) => !pref.sort.some((s) => s.key === k));
  const move = (i, delta) => setPref((p) => {
    const s = [...p.sort];
    const j = i + delta;
    if (j < 0 || j >= s.length) return p;
    [s[i], s[j]] = [s[j], s[i]];
    return { ...p, sort: s };
  });
  const flip = (i) => setPref((p) => {
    const s = [...p.sort];
    s[i] = { ...s[i], dir: s[i].dir === "asc" ? "desc" : "asc" };
    return { ...p, sort: s };
  });
  const remove = (i) => setPref((p) => ({ ...p, sort: p.sort.filter((_, x) => x !== i) }));
  const add = (key) => key && setPref((p) => ({ ...p, sort: [...p.sort, { key, dir: "asc" }] }));

  const save = async () => {
    setSaving(true);
    try {
      const prefs = parseUiPrefs(user);
      const next = { ...prefs, view_prefs: { ...(prefs.view_prefs || {}), [page]: pref } };
      await api.put("/users/me/ui-prefs", { prefs: next });
      await refreshUser();
      push("Vista preferida guardada", "success");
      setOpen(false);
    } catch (e) { push(e.response?.data?.detail || "No se pudo guardar", "error"); }
    finally { setSaving(false); }
  };

  const dirLabel = (s) => {
    if (s.key === "priority") return s.dir === "asc" ? "Alta primero" : "Baja primero";
    if (s.key === "status") return s.dir === "asc" ? "Pendientes primero" : "Completadas primero";
    if (s.key === "due_date" || s.key === "completed_date") return s.dir === "asc" ? "Más próxima primero" : "Más lejana primero";
    return s.dir === "asc" ? "A → Z" : "Z → A";
  };

  return (
    <div className="relative" ref={ref}>
      <button className={`btn-ghost ${open || active ? "bg-brand-50 text-brand-700" : ""}`}
        onClick={() => setOpen((o) => !o)} title="Mi vista: guarda tu ordenamiento preferido">
        <Icon name="eye" size={16} /> Mi vista
        {saved.sort.length > 0 && <span className="ml-1 rounded-tag bg-brand-600 px-1.5 text-[11px] font-bold text-white">{saved.sort.length}</span>}
      </button>

      {open && (
        <div className="absolute right-0 top-full z-30 mt-1.5 w-80 rounded-xl border border-slate-200 bg-white p-3 shadow-pop">
          <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">
            Orden de las tareas (de arriba hacia abajo)
          </div>

          {pref.sort.length === 0 && (
            <p className="mb-2 rounded-lg border border-dashed border-slate-200 px-3 py-3 text-center text-xs text-slate-400">
              Sin criterios: orden por defecto (nuevas → por vencer → en proceso → resto → finalizadas). Agrega uno abajo para personalizarlo.
            </p>
          )}
          <div className="space-y-1.5">
            {pref.sort.map((s, i) => (
              <div key={s.key} className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50/70 px-2 py-1.5">
                <span className="w-4 text-center text-[11px] font-bold text-slate-400">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-xs font-semibold text-slate-700">{LABEL[s.key]}</div>
                  <button className="text-[11px] text-brand-600 hover:underline" onClick={() => flip(i)} title="Invertir dirección">
                    {dirLabel(s)}
                  </button>
                </div>
                <button className="rounded p-0.5 text-slate-400 hover:bg-slate-200 disabled:opacity-30" disabled={i === 0} onClick={() => move(i, -1)} title="Subir">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 15l-6-6-6 6" /></svg>
                </button>
                <button className="rounded p-0.5 text-slate-400 hover:bg-slate-200 disabled:opacity-30" disabled={i === pref.sort.length - 1} onClick={() => move(i, 1)} title="Bajar">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M6 9l6 6 6-6" /></svg>
                </button>
                <button className="rounded p-0.5 text-slate-300 hover:text-rose-500" onClick={() => remove(i)} title="Quitar"><Icon name="x" size={13} /></button>
              </div>
            ))}
          </div>

          {available.length > 0 && (
            <select className="input mt-2 h-8 w-full text-xs" value="" onChange={(e) => add(e.target.value)}>
              <option value="">+ Agregar criterio…</option>
              {available.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          )}

          <label className="mt-3 flex items-center gap-2 text-xs text-slate-600">
            <input type="checkbox" className="h-4 w-4 accent-brand-600" checked={pref.hideCompleted}
              onChange={(e) => setPref((p) => ({ ...p, hideCompleted: e.target.checked }))} />
            Ocultar tareas completadas
          </label>

          <div className="mt-3 flex items-center justify-between gap-2">
            <button className="text-xs text-slate-400 hover:text-rose-500" onClick={() => setPref(DEFAULT_VIEW_PREF)}>Restablecer</button>
            <div className="flex gap-2">
              <button className="btn-ghost text-xs" onClick={() => setOpen(false)}>Cancelar</button>
              <button className="btn-primary text-xs" disabled={saving} onClick={save}>{saving ? "Guardando…" : "Guardar"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
